import type { PaywallConfig, PaywallFeature } from '@/src/types/paywall';

export type BillingCycle = 'monthly' | 'annual';

export interface CatalogPlan {
  price_id: string;
  unit_amount: number;
  currency: string;
  interval: string;
  interval_count: number;
  product: {
    product_id: string;
    name: string;
    description: string;
    metadata?: Record<string, unknown>;
  };
  metadata?: Record<string, unknown>;
  setup_fee_price_id?: string;
  setup_fee_unit_amount?: number;
  setup_fee_currency?: string;
}

export interface CheckoutSelection {
  feature: PaywallFeature;
  billing: BillingCycle;
  priceId: string;
}

export interface PricingBreakdown {
  recurringUnitAmount: number;
  recurringTotal: number;
  setupUnitAmount: number;
  setupTotal: number;
  chargedToday: number;
  nextCharge: number;
  annualSavingUnitAmount: number;
  currency: string;
}

export interface PaidCheckoutPayload {
  price_id: string;
  quantity: number;
  setup_price_id?: string;
  device_quantity?: number;
}

export const featureForPlan = (plan: CatalogPlan): PaywallFeature | null => {
  const feature = plan.product?.metadata?.feature ?? plan.metadata?.feature;
  return feature === 'hours' || feature === 'gps' || feature === 'combo' ? feature : null;
};

export const billingCycleForPlan = (plan: CatalogPlan): BillingCycle | null => {
  const interval = typeof plan?.interval === 'string' ? plan.interval.toLowerCase() : '';
  const intervalCount = plan?.interval_count;
  if (!Number.isInteger(intervalCount) || intervalCount <= 0) return null;
  if (interval === 'month' && intervalCount === 1) return 'monthly';
  if (interval === 'year' && intervalCount === 1) return 'annual';
  if (interval === 'month' && intervalCount === 12) return 'annual';
  return null;
};

export const checkoutSelectionForCatalog = (
  catalog: CatalogPlan[],
  selection: CheckoutSelection,
): CatalogPlan | null =>
  catalog.find(
    plan =>
      plan.price_id === selection.priceId &&
      featureForPlan(plan) === selection.feature &&
      billingCycleForPlan(plan) === selection.billing,
  ) ?? null;

export const getTrialDays = (
  plan: CatalogPlan,
  feature: PaywallFeature,
  billing: BillingCycle,
  config: PaywallConfig | null,
): number | null => {
  if (
    feature !== 'hours' ||
    billing !== 'monthly' ||
    billingCycleForPlan(plan) !== 'monthly' ||
    !config
  ) return null;
  // A degraded/partial config is "no trial", not a crash: the pricing surface
  // must still render honest amounts when the paywall endpoint answers thin.
  if (!Array.isArray(config.plans)) return null;
  const eligibility = config.plans.find(item => item.stripe_price_id === plan.price_id);
  if (!eligibility || eligibility.requires_card || eligibility.trial_days <= 0) return null;
  return eligibility.trial_days;
};

/**
 * Trial length as each persona actually experiences it (#289).
 *
 * The owner trial is the no-card one (`create_trial_subscription_no_card`), so
 * a plan that requires a card is not a trial for the owner. The driver
 * self-pay trial DOES require a card upfront and still charges nothing today —
 * `SelfPayCheckoutView` sets `trial_period_days` from the same config — so
 * hiding it from the driver would show a "charged today" amount that Stripe
 * never collects.
 */
export const getPersonaTrial = (
  plan: CatalogPlan,
  feature: PaywallFeature,
  billing: BillingCycle,
  config: PaywallConfig | null,
  persona: 'owner' | 'driver',
): { days: number; requiresCard: boolean } | null => {
  if (persona === 'owner') {
    const days = getTrialDays(plan, feature, billing, config);
    return days == null ? null : { days, requiresCard: false };
  }
  if (
    feature !== 'hours' ||
    billing !== 'monthly' ||
    billingCycleForPlan(plan) !== 'monthly' ||
    !config
  ) return null;
  if (!Array.isArray(config.plans)) return null;
  const eligibility = config.plans.find(item => item.stripe_price_id === plan.price_id);
  if (!eligibility || eligibility.trial_days <= 0) return null;
  return { days: eligibility.trial_days, requiresCard: !!eligibility.requires_card };
};

const assertFiniteNonNegative = (value: number, error: string) => {
  if (!Number.isFinite(value) || value < 0) throw new Error(error);
};

export const buildPaidCheckoutPayload = (
  plan: CatalogPlan,
  feature: PaywallFeature,
  quantity: number,
): PaidCheckoutPayload => {
  if (!Number.isInteger(quantity) || quantity < 1) throw new Error('invalid_quantity');
  if (featureForPlan(plan) !== feature || !plan.price_id) throw new Error('invalid_selection');

  const payload: PaidCheckoutPayload = { price_id: plan.price_id, quantity };
  if (feature === 'gps' || feature === 'combo') {
    if (
      !plan.setup_fee_price_id ||
      plan.setup_fee_unit_amount == null ||
      !Number.isFinite(plan.setup_fee_unit_amount) ||
      plan.setup_fee_unit_amount <= 0
    ) {
      throw new Error('setup_fee_unavailable');
    }
    payload.setup_price_id = plan.setup_fee_price_id;
    payload.device_quantity = quantity;
  }
  return payload;
};

export const buildPricingBreakdown = ({
  plan,
  monthlyPlan,
  feature,
  billing,
  quantity,
  trialDays,
}: {
  plan: CatalogPlan;
  monthlyPlan: CatalogPlan | null;
  feature: PaywallFeature;
  billing: BillingCycle;
  quantity: number;
  trialDays: number | null;
}): PricingBreakdown => {
  if (!Number.isInteger(quantity) || quantity < 1) throw new Error('invalid_quantity');
  if (featureForPlan(plan) !== feature || billingCycleForPlan(plan) !== billing) {
    throw new Error('invalid_selection');
  }
  assertFiniteNonNegative(plan.unit_amount, 'invalid_recurring_price');
  if (!plan.currency) throw new Error('invalid_currency');

  let setupUnitAmount = 0;
  if (feature === 'gps' || feature === 'combo') {
    const setupCurrency = plan.setup_fee_currency ?? plan.currency;
    if (
      !plan.setup_fee_price_id ||
      plan.setup_fee_unit_amount == null ||
      !Number.isFinite(plan.setup_fee_unit_amount) ||
      plan.setup_fee_unit_amount <= 0 ||
      setupCurrency.toLowerCase() !== plan.currency.toLowerCase()
    ) {
      throw new Error('setup_fee_unavailable');
    }
    setupUnitAmount = plan.setup_fee_unit_amount;
  }

  const recurringTotal = plan.unit_amount * quantity;
  const setupTotal = setupUnitAmount * quantity;
  const hasTrial = feature === 'hours' && billing === 'monthly' && trialDays != null && trialDays > 0;

  let annualSavingUnitAmount = 0;
  if (
    billing === 'annual' &&
    monthlyPlan &&
    featureForPlan(monthlyPlan) === feature &&
    billingCycleForPlan(monthlyPlan) === 'monthly' &&
    monthlyPlan.currency.toLowerCase() === plan.currency.toLowerCase()
  ) {
    annualSavingUnitAmount = Math.max(0, monthlyPlan.unit_amount * 12 - plan.unit_amount);
  }

  return {
    recurringUnitAmount: plan.unit_amount,
    recurringTotal,
    setupUnitAmount,
    setupTotal,
    chargedToday: hasTrial ? 0 : recurringTotal + setupTotal,
    nextCharge: recurringTotal,
    annualSavingUnitAmount,
    currency: plan.currency,
  };
};
